import React, { createContext } from 'react';
import { LegacyComparisonApiService } from '../../services/comparator-api/comparator-legacy-api';
import {
  IComparatorApiService,
  IComparatorApiServiceParameters,
} from '../../services/comparator-api';
import { WizardState, Step } from '../../state-machines/wizard-machine';
import { IPageBuilderContext } from '../page-builder/page-builder-context';
import { IWizardContext } from '../wizard/wizard-context';
import { get as _get, set as _set } from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Module } from '../../config';

export interface ComparatorStep extends Step {
  path: string;
  skipPreviousFieldErrors?: boolean;
}

export type NavigateArgs =
  | {
      stepIndex: number;
      path?: string;
    }
  | {
      path: string;
      stepIndex?: number;
    };
export interface ActionContexts {
  comparator: IComparatorContext;
  wizard: IWizardContext['wizardService'];
  pageBuilder: IPageBuilderContext;
}
export interface QueryActionFn {
  (queryParamValue: string, contexts: ActionContexts): void;
}
export interface IBaseComparatorContext {
  name: string;
  navigationMode: 'slug' | 'url';
  rootUrl: string;
  getStateFromLocalStorage: () => Partial<WizardState> | null;
  persistStateInLocalStorage: (state: Partial<WizardState>) => void;
  /**
   * function that handle the actual router navigation. Internally it has to check the navigationMode to determine the correct path
   * and trigger the right location change (ie assign(url) || assign(rootUrl + searchParams))
   */
  navigate?: (to: NavigateArgs, options?: { reload: boolean }) => void;
  defaultStep?: string;
  router: {
    // TODO: move those params in the router object config
    // rootUrl?: string;
    // navigationMode?: 'url' | 'slug';
    // navigate: (to: NavigateArgs) => void;

    // todo: refactor the "slug" param for it to use this general queryActions config
    queryActions?: {
      [param: string]: {
        action: QueryActionFn;
      };
    };
  };
  api: IComparatorApiService;
  storage?: ReturnType<typeof getStorage>;
  modules: Module[];
  allModulesLoaded: boolean;
  setAllModulesLoaded: React.Dispatch<boolean> | (() => void);
  loadedModules: string[];
  setLoadedModules: React.Dispatch<string[]> | (() => void);
  isPartner?: boolean;
}

export const getStorage = ({
  getStateFromLocalStorage,
  persistStateInLocalStorage,
}) => {
  const get = (path?: string) => {
    const state = getStateFromLocalStorage();
    if (!path) return state;
    return _get(state, path, undefined);
  };
  const set = (path: string, value: any): Partial<WizardState> | undefined => {
    const state = getStateFromLocalStorage();
    if (!state) return undefined;
    const newState = _set(state, path, value);
    try {
      persistStateInLocalStorage(newState);
      return newState;
    } catch (e) {
      console.warn('could not set new value ', value, 'at path', path);
      return undefined;
    }
  };
  const getStep = (nameOrIndex: string | number): Step | undefined => {
    if (typeof nameOrIndex === 'number') {
      return get('context.initialSteps[' + nameOrIndex + ']');
    } else {
      const steps: Step[] | undefined = get('context.initialSteps');
      if (!steps) return undefined;
      return steps.find((s) => s.name === nameOrIndex);
    }
  };

  const setStep = (
    nameOrIndex: string | number,
    value: any
  ): Partial<WizardState> | undefined => {
    if (typeof nameOrIndex === 'number') {
      return set('context.initialSteps[' + nameOrIndex + ']', value);
    } else {
      const steps: Step[] | undefined = get('context.initialSteps');
      if (!steps) return undefined;
      const index = steps.findIndex((s) => s.name === nameOrIndex);
      if (index < 0) return undefined;
      return setStep(index, value);
    }
  };
  return {
    get,
    set,
    getStep,
    setStep,
  };
};

export const getNavigateByUrl: (
  navigate: ReturnType<typeof useNavigate>
) => IComparatorContext['navigate'] =
  (navigate) =>
  ({ path }) => {
    if (typeof path !== 'string') return;
    navigate(path);
  };

export const getNavigateBySlug: (
  navigate: ReturnType<typeof useNavigate>,
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  rootUrl: string
) => IComparatorContext['navigate'] =
  (navigate, setSearchParams, rootUrl) =>
  ({ path }, options) => {
    if (typeof path !== 'string') return;
    if (options?.reload === true) {
      if (path.startsWith('/')) location.assign(path);
      else location.assign(rootUrl + '?step=' + path);
    } else {
      /** NOTE: the rootUrl does not apply when a full path is provided */
      if (path.startsWith('/')) navigate(path);
      else {
        setSearchParams({ step: path });
      }
    }
  };

export const apiServiceDefaultParams: IComparatorApiServiceParameters = {
  baseUrl: '',
  credentials: {
    user: '',
    password: '',
    token: '',
  },
};

export type IComparatorContext = IBaseComparatorContext & {
  setComparatorContext?: React.Dispatch<
    React.SetStateAction<IComparatorContext>
  >;
};

export const defaultComparatorContext: IComparatorContext = {
  name: 'kizi-comparator',
  navigationMode: 'slug',
  rootUrl: '/',
  getStateFromLocalStorage: () => null,
  persistStateInLocalStorage: () => null,
  router: {},
  api: new LegacyComparisonApiService(apiServiceDefaultParams),
  modules: [],
  loadedModules: [],
  allModulesLoaded: false,
  setLoadedModules: () => null,
  setAllModulesLoaded: () => null,
  defaultStep: 'start',
  isPartner: false,
};

export const ComparatorContext = createContext<IComparatorContext>(
  defaultComparatorContext
);

export default ComparatorContext;
